import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackTermsEvent } from '@/helpers/analytics';
import { navigateToError } from '@/helpers/navigateToError';
import { api, ApiError } from '@/basedata/apiCall';
import type { ConsentTermsResponse, UseConsentTermsResult } from '@/types/consent';

const TERMS_PATH = '/terms';

export function useConsentTerms(): UseConsentTermsResult {
  const navigate = useNavigate();
  const [data, setData] = useState<ConsentTermsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadTerms = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.consent.getTerms();
      setData(response);
      void trackTermsEvent({
        name: 'terms_load_success',
        params: { version: response.version },
      });
    } catch (err) {
      setData(null);
      void trackTermsEvent({
        name: 'terms_load_error',
        params: err instanceof ApiError ? { status: err.status } : undefined,
      });
      navigateToError(navigate, {
        error: err,
        originPath: TERMS_PATH,
        ...(err instanceof ApiError ? { errorCode: String(err.status) } : {}),
      });
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    void loadTerms();
  }, [loadTerms]);

  return { data, isLoading };
}
